<template>
  <div class="transactions">
    <v-row>
      <v-col>
        <breadcrumb :items="breadcrumbs" />
      </v-col>
    </v-row>
    <v-row align="center">
      <v-col
        sm="12"
        md="6"
      >
        <h3
          v-show="!isLoading"
          class="font-weight-light"
        >
          Some information about <span class="font-weight-medium">{{ details.name }}</span>
        </h3>
        <vue-skeleton-loader
          v-show="isLoading"
          :width="350"
          :height="21"
        />
      </v-col>
      <v-col
        sm="12"
        md="6"
        class="text-right"
      >
        <v-btn
          color="primary"
          outlined
          class="mr-2"
          @click="showCreditCustomerAccountDialog = true"
          @account-credited="getDetails"
        >
          <v-icon
            left
            size="22"
          >
            {{ icons.mdiCash100 }}
          </v-icon>
          Credit Customer
        </v-btn>
        <v-btn
          v-if="!details.bypass_next_monthly_service_charge"
          color="warning"
          outlined
          @click="showGrantBypassDialog = true"
        >
          <v-icon
            left
            size="22"
          >
            {{ icons.mdiClockAlert }}
          </v-icon>
          Grant Service Bypass
        </v-btn>
        <v-btn
          v-else
          color="error"
          outlined
          @click="showRevokeBypassDialog = true"
        >
          <v-icon
            left
            size="22"
          >
            {{ icons.mdiCloseCircle }}
          </v-icon>
          Revoke Service Bypass
        </v-btn>
      </v-col>
    </v-row>
    <br>
    <br>
    <user-details-card
      :user="details"
      :is-loading="isLoading"
    />
    <br>
    <user-connection-fee-details-card
      v-if="details && details.should_pay_connection_fee"
      :user="details"
      :is-loading="isLoading"
    />
    <br>
    <meter-details-card
      :meter="details.meter"
      :is-loading="isLoading"
    />
    <br>
    <initial-debt-payments
      v-if="details && details.unaccounted_debts_count > 0"
      :is-loading="isLoading"
      :payments="details.unaccounted_debts"
    /><br>
    <div v-if="details.meter.type !== null && details.meter.type.name == 'Prepaid'">
      <meter-tokens-table
        :meter-token-url="meterTokenUrl"
        :clickable-rows="false"
        :show-add-meter-dialog="showAddMeterDialog"
        @add-meter-dialog-open="showAddMeterDialog = true"
        @add-meter-dialog-close="showAddMeterDialog = false"
      />
      <br>
      <br>
    </div>
    <div v-else>
      <meter-readings-table
        :meter-reading-url="meterReadingUrl"
        :clickable-rows="false"
        :for-specific-customer="true"
        :default-sort-by="'month'"
        :default-sort-desc="true"
        :show-add-meter-dialog="showAddMeterDialog"
        @add-meter-dialog-open="showAddMeterDialog = true"
        @add-meter-dialog-close="showAddMeterDialog = false"
      />
      <br>
      <br>
      <meter-reading-statistics-card :meter-id="details.meter.id" />
    </div>
    <br>
    <connection-fee-table
      v-if="details && details.should_pay_connection_fee"
      :connection-fee-url="connectionFeeUrl"
      :for-specific-customer="true"
      :clickable-rows="false"
    /><br><br>
    <monthly-service-charge-table
      :monthly-service-charge-url="monthlyServiceChargePaymentsUrl"
      :for-specific-customer="true"
      :clickable-rows="false"
      :user="details"
    /><br><br>
    <transaction-table
      :transaction-url="transactionUrl"
      :clickable-rows="false"
    />
    <br><br>
    <sms-table
      :sms-url="smsUrl"
      :send-sms-dialog="false"
      :for-specific-customer="true"
    />
    <credit-customer-account
      :show-dialog="showCreditCustomerAccountDialog"
      :user="details"
      @close="showCreditCustomerAccountDialog = false"
    />
    <grant-service-charge-bypass-dialog
      :show-dialog="showGrantBypassDialog"
      :user="details"
      @close="showGrantBypassDialog = false"
      @bypass-granted="getDetails"
    />
    <revoke-service-charge-bypass-dialog
      :show-dialog="showRevokeBypassDialog"
      :user="details"
      @close="showRevokeBypassDialog = false"
      @bypass-revoked="getDetails"
    />
  </div>
</template>
<script>
import { mdiCash100, mdiClockAlert, mdiCloseCircle } from '@mdi/js'
import axios from 'axios'
import VueSkeletonLoader from 'skeleton-loader-vue'
import UserDetailsCard from '@/components/details-card/UserDetailsCard.vue'
import UserConnectionFeeDetailsCard from '@/components/details-card/UserConnectionFeeDetailsCard.vue'
import MeterDetailsCard from '@/components/details-card/MeterDetailsCard.vue'
import TransactionTable from '@/components/tables/TransactionTable.vue'
import BreadCrumb from '@/components/partials/BreadCrumb.vue'
import MeterReadingsTable from '@/components/tables/MeterReadingsTable.vue'
import MeterTokensTable from '@/components/tables/MeterTokensTable.vue'
import MeterReadingStatisticsCard from '@/components/details-card/MeterReadingStatisticsCard.vue'
import ConnectionFeeTable from '@/components/tables/ConnectionFeeTable.vue'
import InitialDebtPayments from '@/components/details-card/InitialDebtPayments.vue'
import SmsTable from '@/components/tables/SmsTable.vue'
import CreditCustomerAccount from '@/components/dialogs/CreditCustomerAccount.vue'
import GrantServiceChargeBypassDialog from '@/components/dialogs/GrantServiceChargeBypassDialog.vue'
import RevokeServiceChargeBypassDialog from '@/components/dialogs/RevokeServiceChargeBypassDialog.vue'
import MonthlyServiceChargeTable from '@/components/tables/MonthlyServiceChargeTable.vue'

export default {
  components: {
    breadcrumb: BreadCrumb,
    UserDetailsCard,
    MeterDetailsCard,
    TransactionTable,
    MeterReadingsTable,
    VueSkeletonLoader,
    MeterReadingStatisticsCard,
    MeterTokensTable,
    ConnectionFeeTable,
    InitialDebtPayments,
    SmsTable,
    UserConnectionFeeDetailsCard,
    CreditCustomerAccount,
    GrantServiceChargeBypassDialog,
    RevokeServiceChargeBypassDialog,
    MonthlyServiceChargeTable,
  },
  data() {
    return {
      showCreditCustomerAccountDialog: false,
      showGrantBypassDialog: false,
      showRevokeBypassDialog: false,
      search: '',
      details: { user: { name: '' }, meter: { type: {} }, transaction: [] },
      isLoading: false,
      isPaymentsSummaryLoading: true,
      isSummaryYearsLoading: true,
      showAddMeterDialog: false,
      customerId: this.$route.params.id,
      transactionUrl: `transactions?user_id=${this.$route.params.id}`,
      breadcrumbs: [
        {
          text: 'Dashboard',
          disabled: false,
          to: { name: 'dashboard' },
        },
        {
          text: 'Customers',
          disabled: false,
          to: { name: 'customers' },
        },
      ],
      summary_years: [],
      selected_summary_year: '',
      payments_summary: [],
      icons: {
        mdiCash100,
        mdiClockAlert,
        mdiCloseCircle,
      },
    }
  },
  computed: {
    meterReadingUrl() {
      return `meter-readings?user_id=${this.customerId}`
    },
    meterTokenUrl() {
      return `meter-tokens?meter_id=${this.details.meter.id}`
    },
    connectionFeeUrl() {
      return `connection-fees?user_id=${this.customerId}`
    },
    smsUrl() {
      return `sms?user_id=${this.customerId}`
    },
    monthlyServiceChargePaymentsUrl() {
      return `monthly-service-charges?user_id=${this.customerId}`
    },
  },
  watch: {
    selected_summary_year() {
      this.getPaymentsSummary()
    },
  },
  created() {
    this.selected_summary_year = (new Date()).getFullYear().toString()
  },
  mounted() {
    if (this.customerId) {
      this.breadcrumbs.push(
        {
          text: this.customerId,
          disabled: true,
          to: { name: 'more-customer-details' },
        },
      )
    }
    this.getDetails()
    this.getPaymentsSummary()
    this.getPaymentsSummaryYears()
  },
  methods: {
    getDetails() {
      this.isLoading = true
      axios
        .get(`users/${this.customerId}`)
        .then(response => {
          this.details = response.data
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    getPaymentsSummary() {
      axios
        .get(`user-billing-report/${this.customerId}?year=${this.selected_summary_year}`)
        .then(response => {
          if (Object.keys(response.data).length > 0) {
            this.payments_summary = [response.data]
          }
          this.isPaymentsSummaryLoading = false
        })
        .catch(error => {
          this.isPaymentsSummaryLoading = false
          this.$notification.error(error.response.data.message)
        })
    },
    getPaymentsSummaryYears() {
      axios
        .get(`user-billing-report-years/${this.customerId}`)
        .then(response => {
          if (Object.keys(response.data).length > 0) {
            this.summary_years = response.data
          }
          this.isSummaryYearsLoading = false
        })
        .catch(error => {
          this.isSummaryYearsLoading = false
          this.$notification.error(error.response.data.message)
        })
    },
  },
}
</script>
